import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import { useToast } from '../ux/Toast';
import { UserAPI } from '@shared/types';
import tracking from '../../lib/tracking';
import { useTheme } from '../../lib/useTheme';
import { useCurrentBookingId } from './getbookingContext';
import { formatPrice } from '../../utils/priceUtils';
import { formatDate, DateFormats } from '../../utils/dateUtils';
import { useT } from '../../lib/i18n';
import { Loader } from '../Loader';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface PaymentFormInnerProps {
  onSuccess: () => void;
  onCancel?: () => void;
  paymentOption: UserAPI.PaymentOption;
  startDate?: Date | null;
}

function PaymentFormInner({ onSuccess, onCancel, paymentOption, startDate }: PaymentFormInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const { t } = useT();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const bookingId = useCurrentBookingId();
  const { price: amount, currency, type } = paymentOption;

  // Track payment form view on mount
  useEffect(() => {
    tracking.paymentFormViewed(amount / 100, currency ?? 'usd');
  }, [amount, currency]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit the form to validate
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || t('payment.validationFailed'));
        tracking.paymentError(submitError.message || 'validation_failed', 'card');
        setIsProcessing(false);
        return;
      }
      const returnUrl = `${window.location.origin}/app/booking/${bookingId}?refetchUntilActive=true`;

      // For subscriptions, use confirmSetup; for one-time payments, use confirmPayment
      if (type === 'subscription') {
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: 'if_required',
        });

        if (error) {
          setErrorMessage(error.message || t('payment.setupFailed'));
          toast.error(error.message || t('payment.setupFailed'));
          tracking.paymentError(error.message || 'setup_failed', 'card');
        } else if (setupIntent && setupIntent.status === 'succeeded') {
          onSuccess();
          tracking.purchase(paymentOption, setupIntent.id);
        }
      } else {
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: 'if_required',
        });

        if (error) {
          setErrorMessage(error.message || t('payment.paymentFailed'));
          toast.error(error.message || t('payment.paymentFailed'));
          tracking.paymentError(error.message || 'payment_failed', 'card');
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          onSuccess();
          tracking.purchase(paymentOption, paymentIntent.id);
        }
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      setErrorMessage(error.message || t('payment.unexpectedError'));
      toast.error(error.message || t('payment.unexpectedError'));
      tracking.paymentError(error.message || 'unexpected_error', 'card');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-dark-muted dark:text-neutral-400">{t('payment.totalAmount')}</span>
            <div className="text-right">
              {paymentOption.original_amount && paymentOption.original_amount > amount ? (
                <>
                  <div className="text-sm text-dark-muted dark:text-neutral-500 line-through">
                    {formatPrice(paymentOption.original_amount, currency)}
                  </div>
                  <div className="text-2xl font-bold text-primary-800 dark:text-primary-400">
                    {formatPrice(amount, currency)}
                  </div>
                </>
              ) : (
                <span className="text-2xl font-bold dark:text-neutral-100">{formatPrice(amount, currency)}</span>
              )}
            </div>
          </div>
          {paymentOption.original_amount && paymentOption.original_amount > amount && (
            <div className="text-xs text-primary-800 dark:text-primary-400 text-right">
              {t('payment.couponApplied')} {paymentOption.coupon_once ? `(${t('payment.firstPaymentOnly')})` : ''}
            </div>
          )}
        </div>

        <PaymentElement
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
            },
            fields: {
              billingDetails: {
                address: {
                  country: 'auto',
                },
              },
            },
          }}
        />
      </div>

      {/* Start Date Message */}
      {type === 'subscription' && startDate && (
        <div className="text-sm text-center p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg">
          <p className="font-medium text-primary-900 dark:text-primary-300">
            💳 {t('payment.paymentWillStart')} {formatDate(new Date(startDate), DateFormats.full)}
          </p>
          <p className="text-xs text-primary-700 dark:text-primary-400 mt-1">
            {t('payment.activeSubscriptionNote')}
          </p>
        </div>
      )}

      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-4"
        >
          <p className="text-red-500 text-sm">{errorMessage}</p>
        </motion.div>
      )}

      <div className="flex space-x-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary py-4 flex-1"
            disabled={isProcessing}
          >
            {t('common.buttons.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="btn btn-primary py-4 flex-1"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              {t('payment.processing')}
            </span>
          ) : (
            `${t('payment.pay')} ${formatPrice(amount, currency)}`
          )}
        </button>
      </div>

      <div className="text-center text-sm text-dark-muted dark:text-neutral-400">
        <p>{t('payment.securePayment')}</p>
      </div>
    </form>
  );
}

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
  onCancel?: () => void;
  paymentOption: UserAPI.PaymentOption;
  startDate?: Date | null;
}

export function PaymentForm({
  clientSecret,
  onSuccess,
  onCancel,
  paymentOption,
  startDate,
}: PaymentFormProps) {
  const { t } = useT();
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const { effectiveTheme } = useTheme();

  useEffect(() => {
    stripePromise.then(() => setStripeLoaded(true));
  }, []);

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-dark-muted dark:text-neutral-400">{t('payment.loadingForm')}</div>
      </div>
    );
  }

  if (!stripeLoaded) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  const isDark = effectiveTheme === 'dark';

  const options = {
    clientSecret,
    appearance: {
      theme: isDark ? 'night' as const : 'flat' as const,
      variables: {
        colorPrimary: '#cf029c',
        colorText: isDark ? '#f5f5f5' : '#000',
        colorBackground: isDark ? '#262626' : '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
      rules: isDark ? {
        '.Input': {
          backgroundColor: '#404040',
          borderColor: '#525252',
          color: '#f5f5f5',
        },
        '.Input:focus': {
          borderColor: '#cf029c',
        },
        '.Label': {
          color: '#d4d4d4',
        },
        '.Tab': {
          backgroundColor: '#262626',
          borderColor: '#404040',
          color: '#d4d4d4',
        },
        '.Tab--selected': {
          backgroundColor: '#404040',
          borderColor: '#cf029c',
          color: '#f5f5f5',
        },
        '.Tab:hover': {
          backgroundColor: '#404040',
        },
      } : undefined,
    },
  };

  return (
    <Elements stripe={stripePromise} options={options} key={effectiveTheme}>
      <PaymentFormInner
        onSuccess={onSuccess}
        onCancel={onCancel}
        paymentOption={paymentOption}
        startDate={startDate}
      />
    </Elements>
  );
}

